import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Slider from "react-slick";
import { all_routes } from "../../router/all_routes";
import { handleApiError } from "../../../utility/handle-api-error";
import apiInstance from "../../../utility/apiInstance";
import { stripNonNumeric } from "../../../utility/inputValidators";

const InstitutionRegister: React.FC = () => {

    interface InstitutionData {
        schoolName: string;
        country: string;
        phoneNumber: string;
        about: string;
        founded: string;
        location: string;
        schoolType: string;
    }

    const [institutionData, setInstitutionData] = useState<InstitutionData>({
        schoolName: '',
        country: '',
        phoneNumber: '',
        about: '',
        founded: '',
        location: '',
        schoolType: ''
    });

    const [error, setError] = useState<string | null>(null);
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [termsCond, setTermsCond] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const route = all_routes;
    const navigate = useNavigate();

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name } = event.target;
        let { value } = event.target;
        if (name === 'phoneNumber') {
            value = stripNonNumeric(value);
        }
        setInstitutionData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        if (errorFields.includes(name)) {
            setErrorFields((prev) => prev.filter((f) => f !== name));
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const loginSlider = {
        dots: true,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setLoading(true);

        // Validation
        if (!institutionData.schoolName || !institutionData.country || !institutionData.phoneNumber) {
            const invalid: string[] = [];
            const messages: Record<string, string> = {};
            if (!institutionData.schoolName) {
                invalid.push('schoolName');
                messages.schoolName = 'School name is required.';
            }
            if (!institutionData.country) {
                invalid.push('country');
                messages.country = 'Country is required.';
            }
            if (!institutionData.phoneNumber) {
                invalid.push('phoneNumber');
                messages.phoneNumber = 'Phone number is required.';
            }
            setErrorFields(invalid);
            setFieldErrors(messages);
            setLoading(false);
            return;
        }
        setErrorFields([]);
        setFieldErrors({});

        if (!termsCond) {
            setError("Please agree to Terms of Service and Privacy Policy.");
            setLoading(false);
            return;
        }

        try {
            const response = await apiInstance.post('/api/institutions', institutionData);
            // Navigate to success page or login
            const Path = route.login;
            navigate(Path);
        } catch (error: any) {
            setError(handleApiError(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Main Wrapper */}
            <div className="main-wrapper">
                <div className="login-content">
                    <div className="row">
                        {/* Login Banner */}
                        <div className="col-md-6 login-bg d-none d-lg-flex">
                            <Slider {...loginSlider} className="login-carousel">
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/auth/auth-1.svg"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Welcome to <br />
                                                Insta<span className="text-secondary">Admissions</span>{" "}
                                                Programs.
                                            </h3>
                                            <p>
                                                Register your institution and join our comprehensive platform designed to manage, deliver, and track academic programs with precision and ease.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/banner-01.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Empower Your <br />
                                                Educational <span className="text-secondary">Institution</span>
                                            </h3>
                                            <p>
                                                Transform your institution's digital presence and streamline academic operations with our advanced management system built for modern educational excellence.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/register-img.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Scale Your <br />
                                                Academic <span className="text-secondary">Programs</span>
                                            </h3>
                                            <p>
                                                Join thousands of institutions worldwide using our platform to enhance student engagement, improve program delivery, and achieve academic excellence at scale.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Slider>
                        </div>
                        {/* /Login Banner */}
                        <div className="col-md-6 login-wrap-bg">
                            {/* Login */}
                            <div className="login-wrapper">
                                <div className="loginbox">
                                    <div className="w-100">
                                        <div className="d-flex align-items-center justify-content-between login-header">
                                            <ImageWithBasePath
                                                src="assets/img/logo-white.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                            <Link to={route.homesix} className="link-1">
                                                Back to Home
                                            </Link>
                                        </div>
                                        <h1 className="fs-32 fw-bold topic">Register Institution</h1>
                                        <form onSubmit={handleSubmit} className="mb-3 pb-3">
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    School Name<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        name="schoolName"
                                                        value={institutionData.schoolName}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('schoolName') ? 'is-invalid' : ''}`}
                                                        placeholder="Enter school name"
                                                    />
                                                    <span>
                                                        <i className="isax isax-building-4 input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.schoolName && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.schoolName}</div>
                                                )}
                                            </div>

                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Country<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <select
                                                        name="country"
                                                        value={institutionData.country}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('country') ? 'is-invalid' : ''}`}
                                                    >
                                                        <option value="">Select Country</option>
                                                        <option value="CA">Canada</option>
                                                    </select>
                                                    <span>
                                                        <i className="isax isax-global input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.country && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.country}</div>
                                                )}
                                            </div>

                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Phone Number<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="tel"
                                                        name="phoneNumber"
                                                        value={institutionData.phoneNumber}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('phoneNumber') ? 'is-invalid' : ''}`}
                                                        placeholder="Enter phone number"
                                                    />
                                                    <span>
                                                        <i className="isax isax-call input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.phoneNumber && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.phoneNumber}</div>
                                                )}
                                            </div>

                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    About Institution
                                                </label>
                                                <div className="position-relative">
                                                    <textarea
                                                        name="about"
                                                        value={institutionData.about}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                        rows={4}
                                                        placeholder="Tell us about your institution..."
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3 position-relative">
                                                        <label className="form-label">
                                                            Founded Year
                                                        </label>
                                                        <div className="position-relative">
                                                            <input
                                                                type="number"
                                                                name="founded"
                                                                value={institutionData.founded}
                                                                onChange={handleInputChange}
                                                                className="form-control form-control-lg"
                                                                placeholder="e.g. 2000"
                                                                min="1800"
                                                                max="2024"
                                                            />
                                                            <span>
                                                                <i className="isax isax-calendar input-icon text-gray-7 fs-14" />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3 position-relative">
                                                        <label className="form-label">
                                                            School Type
                                                        </label>
                                                        <div className="position-relative">
                                                            <select
                                                                name="schoolType"
                                                                value={institutionData.schoolType}
                                                                onChange={handleInputChange}
                                                                className="form-control form-control-lg"
                                                            >
                                                                <option value="">Select Type</option>
                                                                <option value="Public">Public</option>
                                                                <option value="Private">Private</option>
                                                                <option value="Charter">Charter</option>
                                                                <option value="Online">Online</option>
                                                                <option value="Vocational">Vocational</option>
                                                            </select>
                                                            <span>
                                                                <i className="isax isax-category input-icon text-gray-7 fs-14" />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Location
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        name="location"
                                                        value={institutionData.location}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                        placeholder="Enter institution location"
                                                    />
                                                    <span>
                                                        <i className="isax isax-location input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                <div className="remember-me d-flex align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="flexCheckDefault"
                                                        checked={termsCond}
                                                        onChange={(e) => setTermsCond(e.target.checked)}
                                                    />
                                                    <label
                                                        className="form-check-label mb-0 d-inline-flex remember-me fs-14"
                                                        htmlFor="flexCheckDefault"
                                                    >
                                                        I agree with{" "}
                                                        <Link to={route.termsConditions} className="link-2 mx-2">
                                                            Terms of Service
                                                        </Link>{" "}
                                                        and{" "}
                                                        <Link to={route.privacyPolicy} className="link-2 mx-2">
                                                            Privacy Policy
                                                        </Link>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-secondary btn-lg"
                                                    type="submit"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Registering...
                                                        </>
                                                    ) : (
                                                        <>
                                                            Register Institution <i className="isax isax-arrow-right-3 ms-1" />
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {error && (
                                                <div className="mt-3 text-red fs-14 fw-normal d-flex align-items-center justify-content-center">
                                                    {error}
                                                </div>
                                            )}
                                        </form>

                                        <div className="fs-14 fw-normal d-flex align-items-center justify-content-center">
                                            Already have an account?
                                            <Link to={route.login} className="link-2 ms-1">
                                                {" "}
                                                Login
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Main Wrapper */}
        </>
    );
};

export default InstitutionRegister;